import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableSegmentBreakdownCell } from '@/features/shared/components/tableSystem/TableSegmentBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { uiTokens } from '@/theme/uiTokens';
import { Box, EventOutlinedIcon, ReleasesIcon } from '@/ui';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { resolveStatusChipTone, type GridColDef, type GridRenderCellParams } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { ReleaseListItemDto } from '@flowqa/shared';

type ReleasesColumnsOptions = {
  onProjectSelect: (projectId: string) => void;
  onDeleteRow?: (row: ReleaseListItemDto) => void;
  tagColorByName?: Partial<Record<string, TagColor>>;
  deleteRowPendingId?: string | null;
  onUpdateRowTagsColor?: (input: {
    row: ReleaseListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId?: string | null;
};

const columnFlex = {
  name: 1,
  status: 0.9,
  testingStatus: 0.5,
  features: 1,
  featureHealth: 0.5,
  suites: 1,
  tests: 1,
};

type StatusSegment = { label: string; value: number; color: string };

export const buildReleasesColumns = ({
  onProjectSelect,
  onDeleteRow,
  tagColorByName = {},
  deleteRowPendingId = null,
  onUpdateRowTagsColor,
  updateRowTagsColorPendingId = null,
}: ReleasesColumnsOptions): Array<GridColDef<ReleaseListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const singleSelectOperators = getSingleSelectFilterOperators();
  const nameColumn: GridColDef<ReleaseListItemDto> = {
    field: 'name',
    headerName: 'Release',
    flex: columnFlex.name,
    cellClassName: 'table-name-cell',
    filterOperators: stringOperators,
    renderCell: (params) => (
      <TableCellContainer gap="none">
        <TableNameCell
          primary={{
            label: params.row.name,
            href: `/releases/${params.row.slug || params.row.id}`,
            onClick: () => {
              onProjectSelect(params.row.projectId);
            },
          }}
          tags={resolveEntityTags(params.row).map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, tagColorByName),
          }))}
        />
      </TableCellContainer>
    ),
  };

  const statusColumn: GridColDef<ReleaseListItemDto> = {
    field: 'status',
    headerName: getDetailLabel('status'),
    flex: columnFlex.status,
    type: 'singleSelect',
    align: 'left',
    headerAlign: 'left',
    valueOptions: releaseStatusOptions,
    filterOperators: singleSelectOperators,
    renderCell: (params) => {
      const status = params.row.status;
      const statusTone = resolveStatusChipTone(status);
      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={params.formattedValue?.toString() ?? '-'}
            chipTone={statusTone}
            chipIcon={<Box component={resolveGenericStatusCardIcon(status)} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={formatDate(params.row.plannedFor) || ''}
            durationTitle="Planned For"
            durationIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={formatDate(params.row.releasedAt) || ''}
            timestampTitle={formatDate(params.row.releasedAt, { includeTime: true }) || null}
            timestampRowTitle="Released At"
            timestampIcon={<ReleasesIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const featuresColumn: GridColDef<ReleaseListItemDto> = {
    field: 'features',
    headerName: 'Features',
    flex: columnFlex.features,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<ReleaseListItemDto>) => {
      const summaries = params.row.features?.items ?? [];
      const counts = summaries.reduce<Record<'draft' | 'planned' | 'ready' | 'released', number>>(
        (acc, item) => {
          acc[item.status] += 1;
          return acc;
        },
        { draft: 0, planned: 0, ready: 0, released: 0 },
      );
      const total = counts.draft + counts.planned + counts.ready + counts.released;
      const segments: StatusSegment[] = [
        { label: 'Draft', value: counts.draft, color: uiTokens.statusColors.neutral },
        { label: 'Planned', value: counts.planned, color: uiTokens.statusColors.blue },
        { label: 'Ready', value: counts.ready, color: uiTokens.statusColors.orange },
        { label: 'Released', value: counts.released, color: uiTokens.statusColors.green },
      ];
      return (
        <TableCellContainer gap="none">
          <TableSegmentBreakdownCell
            totalLabel={`${total} feature${total === 1 ? '' : 's'}`}
            rows={segments.map((segment) => ({
              key: segment.label,
              label: `${segment.value.toLocaleString()} ${segment.label.toLowerCase()}`,
              count: segment.value,
              color: segment.color,
            }))}
          />
        </TableCellContainer>
      );
    },
  };

  const suitesColumn: GridColDef<ReleaseListItemDto> = {
    field: 'latestSuiteResults',
    headerName: 'Latest Suites',
    flex: columnFlex.suites,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestSuiteResults?.counts;
      const executionCounts = {
        passed: counts?.passed,
        failed: counts?.failed,
        errored: counts?.errored,
        skipped: counts?.skipped,
        untested: counts?.untested,
      };
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="suite"
            counts={{
              passed: executionCounts.passed ?? 0,
              failed: executionCounts.failed ?? 0,
              errored: executionCounts.errored ?? 0,
              skipped: executionCounts.skipped ?? 0,
              untested: executionCounts.untested ?? 0,
            }}
          />
        </TableCellContainer>
      );
    },
  };

  const testsColumn: GridColDef<ReleaseListItemDto> = {
    field: 'latestTestResults',
    headerName: 'Latest Tests',
    flex: columnFlex.tests,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestTestResults?.counts;
      const executionCounts = {
        passed: counts?.passed,
        failed: counts?.failed,
        errored: counts?.errored,
        skipped: counts?.skipped,
        untested: counts?.untested,
      };
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="test"
            counts={{
              passed: executionCounts.passed ?? 0,
              failed: executionCounts.failed ?? 0,
              errored: executionCounts.errored ?? 0,
              skipped: executionCounts.skipped ?? 0,
              untested: executionCounts.untested ?? 0,
            }}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<ReleaseListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: 0.12,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <TableActionsCell
        rowName={params.row.name}
        editActionLabel={getEntityActionLabel('edit', 'Release')}
        editTagsEntityNoun="release"
        rowTags={params.row.tags ?? []}
        rowTagColors={(params.row.tags ?? []).map((tag) => resolveTagColor(tag, tagColorByName))}
        onUpdateTagsColor={({ tags, colors }) => {
          onUpdateRowTagsColor?.({ row: params.row, tags, colors });
        }}
        updateTagsColorLoading={updateRowTagsColorPendingId === params.row.id}
        deleteActionLabel={getEntityActionLabel('delete', 'Release')}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [nameColumn, statusColumn, featuresColumn, suitesColumn, testsColumn, actionsColumn];
};
